import pako from 'pako';
import ExifReader from 'exifreader';

export interface ImageMetadata {
    prompt: string;
    negativePrompt: string;
    seed: number;
    steps: number;
    scale: number;
    sampler: string;
    width: number;
    height: number;
    model: string;
    characterPrompts?: { id: string; prompt: string; uc: string }[];
}

// 메타데이터 추출 진입점 (PNG/WebP 공용)
export async function extractMetadata(
    imageBlob: Blob
): Promise<ImageMetadata | null> {
    try {
        const arrayBuffer = await imageBlob.arrayBuffer();

        // ExifReader로 태그 로드 (PNG, WebP 모두 지원)
        const tags = ExifReader.load(arrayBuffer);

        // NovelAI 메타데이터는 주로 'UserComment', 'Description', 'Comment' 등에 저장됨
        let jsonString: string | null = null;

        const userComment = tags['UserComment'] as any;
        const imageDescription = tags['ImageDescription'] as any;
        const comment = tags['Comment'] as any;

        // 1. UserComment 체크 (WebP/NovelAI)
        if (userComment && userComment.description) {
            const desc = userComment.description as string;
            // UNICODE/ASCII 헤더 제거
            jsonString = desc.replace(/^UNICODE\x00+/g, '').replace(/^ASCII\x00+/g, '').replace(/^\0+/g, '');
        }
        // 2. ImageDescription 체크
        else if (imageDescription && imageDescription.description) {
            jsonString = imageDescription.description as string;
        }
        // 3. Comment 체크
        else if (comment && comment.description) {
            jsonString = comment.description as string;
        }

        if (jsonString) {
            try {
                // null 문자 제거 후 파싱 시도
                const cleanJson = jsonString.replace(/\0/g, '');

                // 가끔 "ExifMetadata" 같은 래퍼가 있을 수 있으니 JSON 찾기
                const braceStart = cleanJson.indexOf('{');
                const braceEnd = cleanJson.lastIndexOf('}');

                if (braceStart !== -1 && braceEnd !== -1) {
                    const realJson = cleanJson.substring(braceStart, braceEnd + 1);
                    const wrapperJson = JSON.parse(realJson);

                    // WebP의 경우 {"Comment": "{\"prompt\": ...}"} 형태로 래핑되어 있을 수 있음
                    let actualMetadata = wrapperJson;

                    if (wrapperJson.Comment && typeof wrapperJson.Comment === 'string' && wrapperJson.Comment.trim().startsWith('{')) {
                        try {
                            actualMetadata = JSON.parse(wrapperJson.Comment);
                        } catch {
                            // 내부 파싱 실패 시 래퍼 그대로 사용
                        }
                    } else if (wrapperJson.Description && typeof wrapperJson.Description === 'string' && wrapperJson.Description.trim().startsWith('{')) {
                        try {
                            actualMetadata = JSON.parse(wrapperJson.Description);
                        } catch {
                            // 무시
                        }
                    }

                    return parseNovelAIMetadata(actualMetadata);
                }
            } catch (e) {
                console.warn('[Metadata] JSON parse failed:', e);
            }
        }

        // ExifReader 실패 시 스텔스 PNG 시도
        const stealthResult = await extractStealthMetadata(imageBlob);
        if (stealthResult) return stealthResult;

        return null;
    } catch (error) {
        console.error('Metadata extraction error:', error);
        return null;
    }
}

// NovelAI 메타데이터 형식 파싱
function parseNovelAIMetadata(json: Record<string, unknown>): ImageMetadata {
    const prompt = String(json.prompt || json.input || '');

    // 캐릭터 프롬프트 추출 (NovelAI V4 형식)
    let characterPrompts: { id: string; prompt: string; uc: string }[] | undefined;

    // v4_prompt.caption.char_captions 형식 확인
    const v4Prompt = json.v4_prompt as Record<string, unknown> | undefined;
    if (v4Prompt) {
        const caption = v4Prompt.caption as Record<string, unknown> | undefined;
        if (caption) {
            const charCaptions = caption.char_captions as Array<{ char_caption: string; centers: unknown[] }> | undefined;
            if (charCaptions && Array.isArray(charCaptions)) {
                characterPrompts = charCaptions.map((char, idx) => ({
                    id: `char-${idx}`,
                    prompt: char.char_caption || '',
                    uc: '',
                }));
            }
        }

        // v4_negative_prompt에서 캐릭터 UC 추출
        const v4Negative = json.v4_negative_prompt as Record<string, unknown> | undefined;
        if (v4Negative && characterPrompts) {
            const negCaption = v4Negative.caption as Record<string, unknown> | undefined;
            if (negCaption) {
                const negCharCaptions = negCaption.char_captions as Array<{ char_caption: string }> | undefined;
                if (negCharCaptions && Array.isArray(negCharCaptions)) {
                    negCharCaptions.forEach((negChar, idx) => {
                        if (characterPrompts![idx]) {
                            characterPrompts![idx].uc = negChar.char_caption || '';
                        }
                    });
                }
            }
        }
    }

    if (!characterPrompts && json.characterPrompts && Array.isArray(json.characterPrompts)) {
        characterPrompts = json.characterPrompts as { id: string; prompt: string; uc: string }[];
    }

    // Model can be in various fields depending on source
    const modelValue = json.model || json.source || json.Source || null;

    return {
        prompt,
        negativePrompt: String(json.uc || json.negative_prompt || ''),
        seed: Number(json.seed) || 0,
        steps: Number(json.steps) || 0,
        scale: Number(json.scale || json.cfg_scale) || 0,
        sampler: String(json.sampler || ''),
        width: Number(json.width) || 0,
        height: Number(json.height) || 0,
        model: modelValue ? String(modelValue) : '',
        characterPrompts,
    };
}

// 스텔스 PNG 메타데이터 추출 (Alpha 채널)
export async function extractStealthMetadata(
    imageBlob: Blob
): Promise<ImageMetadata | null> {
    try {
        const imageBitmap = await createImageBitmap(imageBlob);
        const canvas = new OffscreenCanvas(imageBitmap.width, imageBitmap.height);
        const ctx = canvas.getContext('2d');

        if (!ctx) return null;

        ctx.drawImage(imageBitmap, 0, 0);
        const imageData = ctx.getImageData(0, 0, imageBitmap.width, imageBitmap.height);
        const { data, width } = imageData;

        // 스텔스 PNG 시그니처 체크 (stealth_pnginfo)
        const signatureBytes: number[] = [];
        for (let i = 0; i < 8 * 8; i++) {
            const x = i % 8;
            const y = Math.floor(i / 8);
            const idx = (y * width + x) * 4 + 3; // Alpha 채널
            signatureBytes.push(data[idx] & 1);
        }

        const signatureStr = bitsToString(signatureBytes);

        if (signatureStr !== 'stealth_') {
            return null; // 스텔스 메타데이터 없음
        }

        // 메타데이터 길이 읽기
        const lengthBits: number[] = [];
        for (let i = 64; i < 64 + 32; i++) {
            const x = i % width;
            const y = Math.floor(i / width);
            const idx = (y * width + x) * 4 + 3;
            lengthBits.push(data[idx] & 1);
        }
        const metadataLength = bitsToInt(lengthBits);

        // 메타데이터 읽기
        const metadataBits: number[] = [];
        for (let i = 96; i < 96 + metadataLength * 8; i++) {
            const x = i % width;
            const y = Math.floor(i / width);
            const idx = (y * width + x) * 4 + 3;
            metadataBits.push(data[idx] & 1);
        }

        const compressedData = bitsToBytes(metadataBits);
        const decompressedData = pako.inflate(new Uint8Array(compressedData));
        const metadataStr = new TextDecoder().decode(decompressedData);
        const json = JSON.parse(metadataStr);

        return parseNovelAIMetadata(json);
    } catch (error) {
        console.error('Stealth metadata extraction error:', error);
        return null;
    }
}

// 비트 배열을 문자열로 변환
function bitsToString(bits: number[]): string {
    let result = '';
    for (let i = 0; i < bits.length; i += 8) {
        let byte = 0;
        for (let j = 0; j < 8; j++) {
            byte |= bits[i + j] << (7 - j);
        }
        result += String.fromCharCode(byte);
    }
    return result;
}

// 비트 배열을 정수로 변환
function bitsToInt(bits: number[]): number {
    let result = 0;
    for (let i = 0; i < bits.length; i++) {
        result |= bits[i] << (31 - i);
    }
    return result >>> 0;
}

// 비트 배열을 바이트 배열로 변환
function bitsToBytes(bits: number[]): number[] {
    const bytes: number[] = [];
    for (let i = 0; i < bits.length; i += 8) {
        let byte = 0;
        for (let j = 0; j < 8; j++) {
            byte |= bits[i + j] << (7 - j);
        }
        bytes.push(byte);
    }
    return bytes;
}
